import { API_BASE } from '@/lib/api';

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileApi = {
  render: (
    el: HTMLElement | string,
    options: Record<string, unknown>,
  ) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  execute: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;
let siteKeyPromise: Promise<string> | null = null;

/** Off-screen but non-zero box — 0×0 + overflow:hidden can trip invisible challenges. */
function createTurnstileHost(): HTMLDivElement {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText =
    'position:fixed;left:0;top:0;width:1px;height:1px;margin:0;padding:0;overflow:visible;opacity:0;pointer-events:none;z-index:-1';
  document.body.appendChild(host);
  return host;
}

function isRetryableTurnstileError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err || '');
  return (
    /\b600010\b/.test(msg) ||
    /\b300\d{3}\b/.test(msg) ||
    /timeout/i.test(msg) ||
    /Turnstile failed$/i.test(msg)
  );
}

/** Load Turnstile api.js (explicit render). Wait for onload — do not call turnstile.ready(). */
export function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile unavailable'));
  }
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const done = () => {
      if (window.turnstile) resolve();
      else reject(new Error('Turnstile unavailable'));
    };
    const fail = () => reject(new Error('Turnstile script failed'));

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-zg-turnstile]',
    );
    if (existing) {
      if (window.turnstile) {
        resolve();
        return;
      }
      existing.addEventListener('load', done, { once: true });
      existing.addEventListener('error', fail, { once: true });
      let tries = 0;
      const poll = () => {
        if (window.turnstile) {
          done();
          return;
        }
        if (tries++ < 40) {
          window.setTimeout(poll, 50);
          return;
        }
        fail();
      };
      poll();
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.dataset.zgTurnstile = '1';
    script.onload = () => {
      script.dataset.loaded = '1';
      done();
    };
    script.onerror = fail;
    document.head.appendChild(script);
  });

  scriptPromise = scriptPromise.catch((err) => {
    scriptPromise = null;
    throw err;
  });

  return scriptPromise;
}

/** Public site key from build env or GET /auth/config. Empty when unset (local). */
export async function getTurnstileSiteKey(): Promise<string> {
  if (import.meta.env.VITE_TURNSTILE_SITE_KEY) {
    return String(import.meta.env.VITE_TURNSTILE_SITE_KEY).trim();
  }
  if (siteKeyPromise) return siteKeyPromise;

  siteKeyPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/config`);
      if (!res.ok) return '';
      const data = (await res.json()) as { turnstileSiteKey?: unknown };
      return typeof data.turnstileSiteKey === 'string'
        ? data.turnstileSiteKey.trim()
        : '';
    } catch {
      return '';
    }
  })();

  return siteKeyPromise;
}

export function getTurnstileApi(): TurnstileApi | undefined {
  return typeof window !== 'undefined' ? window.turnstile : undefined;
}

/** Prefetch script + site key so the contact checkbox is not cold on first click. */
export function prefetchTurnstile(): void {
  void getTurnstileSiteKey()
    .then((key) => (key ? loadTurnstileScript() : undefined))
    .catch(() => {
      /* ignore */
    });
}

function runTurnstileOnce(action: string, sitekey: string): Promise<string> {
  const api = window.turnstile;
  if (!api) return Promise.reject(new Error('Turnstile unavailable'));

  return new Promise((resolve, reject) => {
    const host = createTurnstileHost();
    let widgetId = '';
    let settled = false;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        if (widgetId) api.remove(widgetId);
      } catch {
        /* ignore */
      }
      host.remove();
      fn();
    };

    const timer = window.setTimeout(() => {
      finish(() => reject(new Error('Turnstile timeout')));
    }, 25_000);

    try {
      widgetId = api.render(host, {
        sitekey,
        size: 'invisible',
        action,
        retry: 'auto',
        'retry-interval': 3000,
        callback: (token: string) => {
          if (!token) {
            finish(() => reject(new Error('Turnstile empty token')));
            return;
          }
          finish(() => resolve(token));
        },
        // Return true so Turnstile does not also throw / log after we handle it.
        'error-callback': (code?: string) => {
          const codeStr = code != null ? String(code) : '';
          finish(() =>
            reject(
              new Error(
                codeStr ? `Turnstile failed (${codeStr})` : 'Turnstile failed',
              ),
            ),
          );
          return true;
        },
        'expired-callback': () => {
          finish(() => reject(new Error('Turnstile expired')));
        },
      });
    } catch (e) {
      finish(() =>
        reject(e instanceof Error ? e : new Error('Turnstile failed')),
      );
    }
  });
}

/**
 * Run an invisible Turnstile challenge (one-shot). Returns '' when no site key
 * is configured so local/dev can skip when the server also skips verification.
 * Retries once on transient 600010 / timeout failures.
 */
export async function getTurnstileToken(action = 'contact'): Promise<string> {
  const sitekey = await getTurnstileSiteKey();
  if (!sitekey) return '';

  await loadTurnstileScript();
  if (!window.turnstile) throw new Error('Turnstile unavailable');

  try {
    return await runTurnstileOnce(action, sitekey);
  } catch (err) {
    if (!isRetryableTurnstileError(err)) throw err;
    await new Promise((r) => window.setTimeout(r, 600));
    return runTurnstileOnce(action, sitekey);
  }
}
